import React, { useEffect, useRef, useState } from 'react';
import Highlight from '../../utils/Highlight';

const ServiceTags = ({
  services = [],
  containerClass = '',
  tagClass = '',
  moreClass = '',
  maxVisible, // optional: force a maximum number of visible tags
  maxRows, // optional: max number of rows to display (e.g. 2 for cards)
  highlightQuery = '' // optional: search query for highlighting
}) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(services.length);

  useEffect(() => {
    if (!containerRef.current || !measureRef.current) return undefined;

    let mounted = true;

    const measure = () => {
      if (!mounted) return;
      const container = containerRef.current;
      const measureEl = measureRef.current;
      if (!container || !measureEl) return;

      // clear previous measurement nodes
      measureEl.innerHTML = '';

      // create measurement spans for each service
      services.forEach((s) => {
        const span = document.createElement('span');
        span.className = tagClass || 'service-tag';
        span.textContent = s;
        span.style.display = 'inline-block';
        measureEl.appendChild(span);
      });

      // add a span for an eventual "+N" indicator (text will be set below)
      const moreSpan = document.createElement('span');
      moreSpan.className = moreClass || 'service-more';
      moreSpan.style.display = 'inline-block';
      measureEl.appendChild(moreSpan);

      // try to copy gap from visible container so measurements match
      const containerStyle = window.getComputedStyle(container);
      const gap = containerStyle.gap || containerStyle.columnGap || '6px';
      measureEl.style.gap = gap;
      measureEl.style.display = 'flex';
      measureEl.style.flexWrap = maxRows && maxRows > 1 ? 'wrap' : 'nowrap';

      const availableWidth = Math.max(0, container.getBoundingClientRect().width);

      // measure widths
      const tagNodes = measureEl.querySelectorAll(`.${tagClass}` || '.service-tag');
      const tagWidths = Array.from(tagNodes).map((n) => n.getBoundingClientRect().width);

      // measure +N width using the full max digits (worst case)
      const moreText = `+${services.length}`;
      moreSpan.textContent = moreText;
      const moreWidth = moreSpan.getBoundingClientRect().width;

      // parse gap in px
      const gapPx = parseFloat(gap) || 6;

      // If maxRows is specified (>1), simulate multi-row wrapping
      if (typeof maxRows === 'number' && maxRows > 1 && availableWidth > 0) {
        let rows = 1;
        let rowUsed = 0;
        let fit = 0;

        for (let i = 0; i < tagWidths.length; i++) {
          const w = tagWidths[i];
          const remainingAfter = tagWidths.length - (i + 1);
          // width needed if we include this tag plus the "+N" badge when there are remaining items
          // We check both fitting in current row and needing a new row
          if (rowUsed === 0) {
            // first item in row
            if (w <= availableWidth) {
              rowUsed = w;
              fit++;
            } else {
              // tag wider than container - show it alone
              if (fit === 0) fit = 1;
              break;
            }
          } else {
            const needed = gapPx + w;
            if (rowUsed + needed <= availableWidth) {
              rowUsed += needed;
              fit++;
            } else {
              // need new row
              if (rows >= maxRows) {
                // no more rows - need to check if we can fit "+N" in current row instead
                // We have consumed fit tags; remaining includes current tag
                // Try to see if "+N" badge can fit in current row after fit tags
                // If not, we may need to backtrack one tag to make room for "+N"
                const moreNeeded = gapPx + moreWidth;
                if (rowUsed + moreNeeded > availableWidth && fit > 0) {
                  // need to hide last tag to make room for "+N"
                  fit = Math.max(0, fit - 1);
                }
                break;
              }
              rows++;
              // check if the tag fits in new row (with possible "+N" reserve)
              const reserveForMore = remainingAfter > 0 ? gapPx + moreWidth : 0;
              // if new row + more exceeds width but tag alone fits, we can place tag
              if (w + reserveForMore <= availableWidth || remainingAfter === 0) {
                rowUsed = w;
                fit++;
              } else if (w <= availableWidth) {
                // tag fits but "+N" wouldn't - we still show tag and will reserve on next iteration
                rowUsed = w;
                fit++;
              } else {
                if (fit === 0) fit = 1;
                break;
              }
            }
          }

          // If we are on the last allowed row, check if next tag would overflow and we need to reserve for "+N"
          if (rows === maxRows && i+1 < tagWidths.length) {
            const nextW = tagWidths[i+1];
            const neededNext = gapPx + nextW;
            // if next doesn't fit, we need to ensure current row can accommodate "+N"
            // we will handle this on next loop iteration when it tries to create a new row and fails
            // peek ahead: if next would need new row but no rows left, we should stop now
            const wouldNeedNewRow = rowUsed + neededNext > availableWidth;
            if (wouldNeedNewRow) {
              // if "+N" doesn't fit in current row, backtrack if needed (handled above)
              // break to show "+N"
              // But first check if "+N" fits in current row after trimming last item already done
              // We break here to avoid including next tag
              // The more badge will be shown as +remaining
              // Continue to next iteration which will break due to rows limit, so we can break now
              // Keep fit as is; next iteration would exceed rows, so we break
              // To avoid extra loop, check if there is any remaining that won't fit
              // We already will break on next iteration, so just continue
            }
          }
        }

        // Final adjustment: ensure "+N" badge fits in last row when there are hidden items
        if (fit < tagWidths.length && fit > 0) {
          // calculate used width on last row for fit items (need to recompute row layout for last row)
          // Simplified: if fit items exactly fill rows, ensure more badge has space
          // We already trimmed one when necessary above, but double-check with actual rowUsed
          const remaining = tagWidths.length - fit;
          if (remaining > 0) {
            const moreNeeded = gapPx + moreWidth;
            // if rowUsed + moreNeeded exceeds width, trim one more
            if (rowUsed + moreNeeded > availableWidth) {
              fit = Math.max(1, fit - 1);
            }
          }
        }

        if (fit === 0 && tagWidths.length > 0) fit = 1;

        // honour maxVisible as hard cap as well
        if (typeof maxVisible === 'number') {
          fit = Math.min(fit, Math.max(0, maxVisible));
        }

        setVisibleCount(fit);
        return;
      }

      let used = 0;
      let fit = 0;

      for (let i = 0; i < tagWidths.length; i++) {
        const w = tagWidths[i];
        const nextUsed = used + (fit > 0 ? gapPx : 0) + w;
        const remainingAfter = tagWidths.length - (i + 1);
        const reserve = remainingAfter > 0 ? gapPx + moreWidth : 0;
        if (nextUsed + reserve <= availableWidth) {
          used = nextUsed;
          fit++;
        } else {
          break;
        }
      }

      if (fit === 0 && tagWidths.length > 0) fit = 1; // show at least one

      setVisibleCount(fit);
    };

    // initial measure (delay slightly to allow fonts to load)
    const t = setTimeout(measure, 0);

    // observe resize
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(containerRef.current);
    } else {
      window.addEventListener('resize', measure);
    }

    return () => {
      mounted = false;
      clearTimeout(t);
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', measure);
    };
  }, [services, tagClass, moreClass, maxRows, maxVisible]);

  // compute final visible count honoring maxVisible when provided
  // when maxRows handled the capping inside measure, we only need to apply maxVisible here for legacy single-row mode
  const finalVisible = typeof maxVisible === 'number' && !(typeof maxRows === 'number' && maxRows > 1) ? Math.min(visibleCount, Math.max(0, maxVisible)) : visibleCount;

  return (
    <>
      <div ref={containerRef} className={containerClass}>
        {services.slice(0, finalVisible).map((s, i) => (
          <span key={i} className={tagClass}>
            {highlightQuery ? <Highlight text={s} query={highlightQuery} /> : s}
          </span>
        ))}
        {finalVisible < services.length && (
          <span className={moreClass}>+{services.length - finalVisible}</span>
        )}
      </div>
      <div
        ref={measureRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          left: '-9999px',
          top: '-9999px',
          whiteSpace: 'nowrap'
        }}
        aria-hidden
      />
    </>
  );
};

export default ServiceTags;
